/**
 * Swipeable image carousel for country photos.
 * Features:
 * - Native lazy loading with decoding="async"
 * - Skeleton placeholder while loading
 * - Fade-in animation on load
 * - fillContainer mode for fitting parent dimensions
 * - Expandable mode: expands on interaction to show full image
 */

import { useState, useCallback, useEffect, memo, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface CountryImageCarouselProps {
  images: string[];
  countryName: string;
  /** When true, fills parent container height instead of using aspect ratio */
  fillContainer?: boolean;
  /** Callback when expanded state changes - parent can remove overflow-hidden */
  onExpandedChange?: (expanded: boolean) => void;
}


/**
 * Individual image with loading state and fade-in animation.
 */
const CarouselImage = memo(function CarouselImage({
  src,
  alt,
  priority = false,
}: {
  src: string;
  alt: string;
  priority?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <>
      {/* Skeleton placeholder */}
      {!loaded && !error && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {/* Error fallback */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground text-sm">
          Failed to load image
        </div>
      )}

      {/* Actual image */}
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className={cn(
          'absolute inset-0 w-full h-full object-cover transition-opacity duration-300',
          loaded ? 'opacity-100' : 'opacity-0'
        )}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </>
  );
});

export function CountryImageCarousel({
  images,
  countryName,
  fillContainer = false,
  onExpandedChange,
}: CountryImageCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  // Toggle expand state
  const handleExpand = useCallback(() => {
    setExpanded(true);
  }, []);

  // Handle click outside to collapse
  useEffect(() => {
    if (!expanded) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };

    // Delay adding listener to avoid immediate trigger from the expand click
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [expanded]);

  // Notify parent of expanded state changes
  useEffect(() => {
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  if (!images || images.length === 0) {
    return null;
  }

  // In fillContainer mode, we need a wrapper that maintains parent height
  // but allows the expanded content to break out
  return (
    <div
      ref={containerRef}
      className={cn(
        "relative",
        fillContainer ? "w-full h-full" : "w-full"
      )}
    >
      {/* Normal state - fills container */}
      <div
        className={cn(
          "w-full overflow-hidden",
          fillContainer ? "h-full" : "rounded-lg"
        )}
        style={{ display: expanded ? 'none' : 'block' }}
      >
        <div ref={expanded ? undefined : emblaRef} className="h-full">
          <div className="flex h-full">
            {images.map((src, index) => (
              <div
                key={index}
                className="relative flex-[0_0_100%] min-w-0 h-full cursor-pointer"
                onClick={handleExpand}
              >
                <CarouselImage
                  src={src}
                  alt={`${countryName} - Image ${index + 1}`}
                  priority={index === 0}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation arrows - normal state */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                scrollPrev();
                handleExpand();
              }}
              className={cn(
                'absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-all',
                !canScrollPrev && 'opacity-30'
              )}
              aria-label="Previous image"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                scrollNext();
                handleExpand();
              }}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-all',
                !canScrollNext && 'opacity-30'
              )}
              aria-label="Next image"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}

        {/* Dot indicators - normal state */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex justify-center gap-1.5">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  emblaApi?.scrollTo(index);
                  handleExpand();
                }}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-all',
                  index === selectedIndex
                    ? 'bg-white w-3'
                    : 'bg-white/50 hover:bg-white/70'
                )}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Expanded state - breaks out of container */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}
            className="absolute top-0 left-0 right-0 z-50 rounded-lg overflow-hidden shadow-2xl ring-1 ring-black/10"
            style={{
              // Extend beyond the map on the left to cover full width
              left: fillContainer ? '-54%' : 0,
              right: 0,
            }}
          >
            {/* Carousel in expanded state */}
            <div ref={expanded ? emblaRef : undefined} className="overflow-hidden rounded-lg">
              <div className="flex">
                {images.map((src, index) => (
                  <div
                    key={index}
                    className="relative flex-[0_0_100%] min-w-0 aspect-[16/9] bg-muted"
                  >
                    <CarouselImage
                      src={src}
                      alt={`${countryName} - Image ${index + 1}`}
                      priority={index <= 1}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation arrows - expanded state */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollPrev();
                  }}
                  className={cn(
                    'absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all',
                    !canScrollPrev && 'opacity-30'
                  )}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    scrollNext();
                  }}
                  className={cn(
                    'absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all',
                    !canScrollNext && 'opacity-30'
                  )}
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Dot indicators - expanded state */}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex justify-center gap-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => {
                      e.stopPropagation();
                      emblaApi?.scrollTo(index);
                    }}
                    className={cn(
                      'w-2 h-2 rounded-full transition-all',
                      index === selectedIndex
                        ? 'bg-white w-5'
                        : 'bg-white/50 hover:bg-white/70'
                    )}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Close hint */}
            <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-[10px] font-medium">
              Click outside to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
